<template>
  <div>

    <b-modal v-model="actionModal"
             lazy
             size="xl"
             :title="actionTypeTitle"
             @ok="performActionOnCurrentSelection"
             :ok-disabled="modalActionDisabled">

      <template #modal-footer="{ ok, cancel, hide }">

        <b-button size="sm"
                  :variant="editingSelectionSelectAllButtonVariant('all')"
                  @click="editingSelectionSelectAll(true)">
          Select All
        </b-button>

        <b-button size="sm"
                  :variant="editingSelectionSelectAllButtonVariant('none')"
                  @click="editingSelectionSelectAll(false)">
          Unselect All
        </b-button>

        <div class="flex-grow-1"></div>

        <b-button size="sm" variant="secondary" @click="cancel()">
          Cancel
        </b-button>

        <b-button size="sm" variant="success" @click="ok()">
          {{ actionTypeButtonTitle }}
        </b-button>

      </template>

      <div v-if="actionType === 'add'">
        <h6>Select the tags to add</h6>

        <b-row>
          <b-col xl="3" lg="12"
                 v-for="tagCategoryData in tagsByCategoryArray">
            <tag-select :field-name="tagCategoryData.name"
                        :tags="tagCategoryData.data.tags"
                        :multiple="true"
                        v-model="selectedTags[tagCategoryData.name]"/>
          </b-col>
        </b-row>
      </div>


      <h6>
        <span v-if="actionType === 'delete'">
          Select the assets to delete the tag <code>{{ editingTag.name }}</code> from
        </span>
        <span v-if="actionType==='add'">
          Select the assets to add the new tag to
        </span>
        <pulse-loader :loading="bulkActionRunning"
                      v-if="bulkActionRunning"
                      size="10px"
                      color="#00C0F4"
                      class="mx-2"/>
      </h6>

      <div class="d-flex flex-wrap align-items-center">
        <div class="cursor-pointer border rounded mr-2 mb-1 p-1" v-for="asset in editingAssets">
          <b-checkbox v-model="editingSelection[asset.id]">
            <div class="img-bg img-bg-1 img-square" :style="`background-image: url('${asset.thumb_url}')`"></div>
          </b-checkbox>
        </div>
      </div>

    </b-modal>

    <div class="d-flex justify-content-between mb-2">
      <b-checkbox v-model="showAssetNumber">Show Asset Numbers</b-checkbox>
      <b-button variant="primary" size="sm" @click="prepareGlobalAdd()">
        Add Tags
      </b-button>
    </div>

    <!--  Tags  -->
    <div class="card mb-3 no-shadow">
      <div class="card-body p-2">
        <div class="d-flex flex-wrap align-items-center">
          <template v-for="(objTags, categoryName) in groupedTagsByCategory">
            <div class="cursor-pointer border rounded mr-2 mb-1 p-1"
                 :class="{'bg-light-green': editing === obj.tag.id}"
                 v-for="obj in objTags">

              <div @click="toggleEditing(obj.tag.id)">
                {{ obj.tag.name }}
                <b-badge v-if="showAssetNumber" variant="primary" title="assets">
                  {{ obj.assets.length }}
                </b-badge>
                <div class="font-italic text-gray-700 font-size-sm">{{ obj.category }}</div>
              </div>

            </div>
          </template>

        </div>
      </div>
    </div>

    <!--  Asset with thumbnails / checkboxes list  -->
    <div class="card mb-3 no-shadow" v-if="editing">
      <div class="card-body p-2">
        <h6>{{ editingTag.name }} <span class="text-secondary font-italic font-size-sm">{{ editingTagCategory }}</span>
        </h6>
        <div class="mb-2 d-flex" style="gap: 5px">
          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('all')"
                    @click="editingSelectionSelectAll(true)">
            Select All
          </b-button>

          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('none')"
                    @click="editingSelectionSelectAll(false)">
            Unselect All
          </b-button>

          <div class="flex-grow-1"></div>

          <b-button variant="danger"
                    size="sm"
                    @click.stop="prepareActionType('delete', editingTagObj)">
            Delete from selection
          </b-button>

          <b-button variant="info"
                    size="sm"
                    @click="prepareActionType('add', editingTagObj)">
            Add new tags to selection
          </b-button>
        </div>

        <div class="d-flex flex-wrap align-items-center">
          <div class="cursor-pointer border rounded mr-2 mb-1 p-1" v-for="asset in editingAssets">
            <b-checkbox v-model="editingSelection[asset.id]">
              <div class="img-bg img-bg-1 img-square" :style="`background-image: url('${asset.thumb_url}')`"></div>
            </b-checkbox>
          </div>
        </div>
      </div>

    </div>


  </div>
</template>

<script>
import {isEqual} from "lodash";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import GeneralMixins from "../../../../mixins/GeneralMixins";
import TagSelect from "../../../Common/TagSelect";
import normalize from "json-api-normalize";
import Attributes from "../../../../models/Attributes";

export default {
  props: ['selectedAssets', 'idKey'],
  components: {PulseLoader, TagSelect},
  mixins: [GeneralMixins],

  data() {
    return {
      editing: null,
      editingSelection: {},
      actionModal: false,
      actionType: null,
      showAssetNumber: true,
      bulkActionRunning: false,
      selectedTags: {},
      loaded: false,
      selectedLoadedAssets: []
    }
  },

  mounted() {
    if (this.idKey === "id") {
      this.loaded = true
      this.selectedLoadedAssets = this.selectedAssets
    } else {
      const assetIds = this.selectedAssets.map(el => el[this.idKey])
      this.$http.post("/curators/bulk/asset_loader", {asset_ids: assetIds}).then(result => {
        this.selectedLoadedAssets = normalize(result.body).get(Attributes.assetWithIncludesAttributes);
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      })
    }
  },

  methods: {
    toggleEditing(tagId) {
      if (this.editing !== tagId) {
        this.startEditing(tagId)
      } else {
        this.stopEditing()
      }
    },

    startEditing(tagId) {
      this.editing = tagId
      this.editingValue = tagId
      this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
        return {
          [asset.id]: this.assetContainsTagId(asset.id, tagId)
        }
      }))
    },

    assetContainsTagId(assetId, tagId) {
      return this.selectedLoadedAssets.find(el => el.id === assetId).tag_ids.includes(tagId)
    },

    newTagsOnSelection(tagIds, selectionIds) {
      const attributes = {
        asset_ids: selectionIds,
        tag_ids: tagIds,
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_tags/add_tags", attributes).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`The tags were added to ${count} assets`, "Success")

        this.selectedLoadedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset.id)[0]
          if (resultAsset) {
            asset.tag_ids = resultAsset.tag_ids
          }
        })

        // this.$emit('change', this.selectedLoadedAssets)
        this.actionModal = false
        this.stopEditing()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    deleteCurrentSelection() {
      const attributes = {
        asset_ids: this.editingSelectionIds,
        tag_ids: [this.editing]
      }

      this.bulkActionRunning = true

      this.$http.post("/curators/bulk/asset_tags/delete_tags", attributes).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`The tags were removed from ${count} assets`, "Success")

        this.selectedLoadedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset.id)[0]
          if (resultAsset) {
            asset.tag_ids = resultAsset.tag_ids
          }
        })

        // this.$emit('change', this.selectedLoadedAssets)
        this.actionModal = false
        this.stopEditing()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    stopEditing() {
      this.editing = null
      this.editingValue = null
      this.actionType = null
      this.createBlankTagSelection()
    },

    // obj is generated by groupedTags()
    prepareActionType(actionType, obj) {
      this.createBlankTagSelection()
      this.actionType = actionType
      if (!this.editingSelection) {
        this.editingSelection = Object.assign({}, ...obj.assets.map(asset => {
          return {
            [asset.id]: true
          }
        }))
      }

      this.actionModal = true
    },

    prepareGlobalAdd() {
      this.createBlankTagSelection()

      this.actionType = 'add'
      this.editingSelection = Object.assign({}, ...this.selectedAssetIds.map(id => {
        return {
          [id]: true
        }
      }))
      this.actionModal = true
      this.editing = null
    },

    createBlankTagSelection() {
      this.selectedTags = Object.keys(this.tagsByCategory).reduce((obj, key) => {
        obj[key] = []
        return obj
      }, {})
    },

    // Selected tags builder
    getSelectedTags: function (asset) {
      const allTags = this.tagsByCategory;

      return Object.keys(allTags).reduce((obj, key) => {
        const categoryTags = allTags[key].tags || [];
        const currentTagIds = asset.tag_ids || [];
        obj[key] = currentTagIds.filter(t => categoryTags.map(tt => tt.id).includes(t));
        return obj
      }, {})
    },

    tag(id) {
      return this.groupedTags[id].tag
    },

    findTag(tagId) {
      return this.allTags.find(tag => tag.id === tagId)
    },

    findCategory(tagId) {
      return Object.keys(this.tagsByCategory).find(key => this.tagsByCategory[key].tags.map(el => el.id).includes(tagId))
    },

    currentTagsForCategory(cat) {
      return this.groupedTagsByCategory[cat]
    },

    performActionOnCurrentSelection(evt) {
      evt.preventDefault()

      if (this.actionType === "delete") {
        this.deleteCurrentSelection()
      } else if (this.actionType === "add") {
        const arrays = Object.keys(this.selectedTags).map(key => this.selectedTags[key])
        this.newTagsOnSelection([].concat.apply([], arrays), this.editingSelectionIds)
      }
    },

    editingSelectionSelectAllButtonVariant(type) {
      if (type === 'all') {
        if (this.editingSelectionAllSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      } else {
        if (this.editingSelectionNoneSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      }
    },

    editingSelectionSelectAll(value) {
      for (const key in this.editingSelection) {
        this.editingSelection[key] = value
      }
    },
  },

  computed: {

    editingTag() {
      const tag = this.findTag(this.editing)
      if (tag) {
        return tag
      } else {
        return {}
      }
    },

    editingTagCategory() {
      return this.findCategory(this.editing)
    },

    editingTagObj() {
      return this.groupedTags[this.editing]
    },

    tagsByCategory() {
      return window.tagStore.tagsByCategory;
    },

    tagsByCategoryArray() {
      return Object.keys(this.tagsByCategory).map(k => {
        return {
          name: k,
          data: this.tagsByCategory[k]
        }
      })
    },

    editingSelectionAllSelected() {
      if (!this.editingSelection) {
        return false
      }

      return isEqual(this.editingSelectionIds, Object.keys(this.editingSelection))
    },

    editingSelectionNoneSelected() {
      if (!this.editingSelection) {
        return false
      }

      return isEqual(this.editingSelectionIds, [])
    },

    allTags() {
      const tagArrays = Object.keys(this.tagsByCategory).map(catName => this.tagsByCategory[catName].tags)
      const tagList = [].concat.apply([], tagArrays)
      return [...new Set(tagList)]
    },

    groupedTags() {
      const tags = this.allUniqueTagIds
      return tags.reduce((obj, tagId) => {
        obj[tagId] = {
          assets: this.selectedLoadedAssets.filter(el => el.tag_ids.includes(tagId)),
          tag: this.findTag(tagId),
          category: this.findCategory(tagId)
        }
        return obj
      }, {})
    },

    groupedArrayTags() {
      return Object.entries(this.groupedTags).map(el => {
        return {...el[1], tagId: el[0]}
      }).sort((a, b) => a.tag.name.localeCompare(b.tag.name))
    },

    groupedTagsByCategory() {
      const tags = this.allUniqueTagIds
      return tags.reduce((obj, tagId) => {
        const gt = this.groupedTags[tagId]

        if (!obj[gt.category]) {
          obj[gt.category] = []
        }

        obj[gt.category].push(gt)
        // todo this is sorting each time
        obj[gt.category] = obj[gt.category].sort((a, b) => a.tag.name.localeCompare(b.tag.name))
        return obj
      }, {})
    },

    allTagCategories() {
      return Object.keys(this.tagsByCategory)
    },

    allUniqueTagIds() {
      const tagArrays = this.selectedLoadedAssets.map(el => el.tag_ids).filter(el => el)
      const tagList = [].concat.apply([], tagArrays)
      return [...new Set(tagList)]
    },

    allUniqueTags() {
      return this.allUniqueTagIds.map(id => this.findTag(id))
    },

    actionTypeTitle() {
      if (this.actionType === "delete") {
        return `Delete Tag: ${this.editingTag.name}`
      } else if (this.actionType === "add") {
        return `Add Tags`
      } else {
        return "Invalid Action"
      }
    },

    editingSelectionIds() {
      if (!this.editingSelection) {
        return []
      }

      return Object.keys(this.editingSelection).filter(id => this.editingSelection[id])
    },

    modalActionDisabled() {
      if (this.actionType === 'add') {
        return this.editingSelectionIds.length === 0 || !this.newKeyword
      } else {
        return this.editingSelectionIds.length === 0
      }
    },

    actionTypeButtonTitle() {
      if (this.actionType === "delete") {
        return `Delete`
      } else if (this.actionType === 'add') {
        return `Add`
      }
    },

    selectedAssetIds: function () {
      return this.selectedLoadedAssets.map(el => el.id)
    },

    editingAssets() {
      return this.selectedLoadedAssets;

      if (this.editing) {
        return this.selectedLoadedAssets.filter(a => a.tag_ids.includes(this.editing))
      } else {
        return this.selectedLoadedAssets
      }
    },

  }
}
</script>